import React, { useState } from 'react';
import { Plus, Settings, X } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Badge } from '@epoch-ui/components/Badge';

import { SlackSettingsModal } from '../components/SlackSettingsModal';
import { DisconnectSlackChannelModal } from '../components/DisconnectSlackChannelModal';
import { useEditableEvent } from '../../../hooks/useEditableEvent';
import { EditSlackSettingsModal } from '../components/EditSlackSettingsModal';

export const SlackChannelSettingsSection = (): React.ReactElement => {
  const [isSlackSettingsModalOpen, setIsSlackSettingsModalOpen] = useState(false);
  const [isEditSlackSettingsModalOpen, setIsEditSlackSettingsModalOpen] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const { slackChannelName, scheduledDeletionAt, hasEnded } = useEditableEvent();

  const isCancelled = !!scheduledDeletionAt;
  const isDisabled = isCancelled || hasEnded;

  return (
    <div>
      <div className="tw-flex tw-items-start tw-justify-between">
        <div className={cn(isDisabled && 'tw-opacity-40')}>
          <h2 className="tw-m-0 tw-text-xl tw-font-medium tw-leading-7">Slack channel</h2>
          <p className="tw-m-0 tw-mt-1 tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey">
            Guests will be automatically added to the connected channel when they register.
          </p>
        </div>
        {slackChannelName && (
          <Button
            size="xxs"
            className="tw-gap-1 tw-rounded-xl tw-leading-4"
            variant="secondary"
            data-testid="settings-button"
            onClick={(): void => setIsEditSlackSettingsModalOpen(true)}
            disabled={isDisabled}
          >
            <Settings className="tw-text-black" size={16} />
            Settings
          </Button>
        )}
      </div>
      {slackChannelName ? (
        <div className="tw-mt-4">
          <Badge size="lg">
            #{slackChannelName}
            <Button
              size="none"
              onClick={(): void => setIsDisconnectModalOpen(true)}
              data-testid="disconnect-button"
              className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-primary"
              disabled={isDisabled}
            >
              <X size={16} />
            </Button>
          </Badge>
        </div>
      ) : (
        <Button
          variant="secondary"
          size="xxs"
          onClick={(): void => setIsSlackSettingsModalOpen(true)}
          data-testid="add-channel-button"
          className="tw-mt-4 tw-w-[120px] tw-rounded-xl tw-border-[2px] tw-border-dashed tw-border-border-grey tw-bg-transparent tw-py-2 tw-text-dark-black hover:tw-bg-background-grey"
          disabled={isDisabled}
        >
          <Plus className="tw-h-5 tw-w-5" />
        </Button>
      )}

      <SlackSettingsModal
        isOpen={isSlackSettingsModalOpen}
        onClose={(): void => setIsSlackSettingsModalOpen(false)}
      />
      <EditSlackSettingsModal
        isOpen={isEditSlackSettingsModalOpen}
        onClose={(): void => setIsEditSlackSettingsModalOpen(false)}
      />
      <DisconnectSlackChannelModal
        isOpen={isDisconnectModalOpen}
        onClose={(): void => setIsDisconnectModalOpen(false)}
      />
    </div>
  );
};
